import { useEffect, useRef } from "react";

/**
 * Type definition for a timer reference
 */
type Timer = ReturnType<typeof setTimeout>;

/**
 * Type definition for a generic function that can accept any arguments
 */
type SomeFunction = (...args: any[]) => void;

/**
 * A custom hook that provides debounce functionality for function calls
 *
 * @param func - The function to debounce
 * @param delay - The delay in milliseconds before the function is called (default: 1000ms)
 * @returns A debounced version of the provided function
 *
 * @example
 * // Usage example
 * const debouncedSearch = useDebounce((term) => {
 *   fetchSearchResults(term);
 * }, 500);
 */
function useDebounce<Func extends SomeFunction>(func: Func, delay = 1000) {
  const timer = useRef<Timer>();

  // Clean up the timer when the component unmounts
  useEffect(() => {
    return () => {
      if (!timer.current) return;
      clearTimeout(timer.current);
    };
  }, []);

  // Return the debounced function
  return ((...args) => {
    const newTimer = setTimeout(() => {
      func(...args);
    }, delay);

    // Clear the previous timer before setting a new one
    if (timer.current) clearTimeout(timer.current);
    timer.current = newTimer;
  }) as Func;
}

export default useDebounce;
